import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState, ReactNode } from "react";

interface ApiSectionProps {
  title: string;
  description?: string;
  badge?: string;
  badgeVariant?: "default" | "success" | "warning" | "destructive";
  defaultOpen?: boolean;
  children: ReactNode;
}

export function ApiSection({
  title,
  description,
  badge,
  badgeVariant = "default",
  defaultOpen = false,
  children,
}: ApiSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const badgeColors = {
    default: "bg-muted text-muted-foreground",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    destructive: "bg-destructive/10 text-destructive",
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-muted/50",
          isOpen && "border-b border-border"
        )}
      >
        <div className="flex items-center gap-3">
          <h4 className="font-semibold text-foreground">{title}</h4>
          {badge && (
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full",
                badgeColors[badgeVariant]
              )}
            >
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            "w-5 h-5 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 fm-expand">
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
